import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { BottomSheet } from '@/components/ui/dialog';
import { BotTokenReplace } from '@/components/BotTokenReplace';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import {
  CountryPickCard,
  Field,
  LegalDocsCard,
  LogChannelCard,
  MirrorsToggleCard,
  RequiredChannelsCard,
  StarsTopupCard,
  StatusPageCard,
  WebappToggleCard,
  MenuButtonCard,
  AskLanguageCard,
} from '@/components/bot-settings/cards';
import {
  LoadError,
  NoBots,
  ScreenHeader,
  Section,
  useBackToHub,
  useSaveButton,
  useSettingsBot,
} from '@/components/bot-settings/screen';

/**
 * «Настройки бота»: поддержка, документы, обязательные каналы, что видят клиенты,
 * логи, токен и удаление. Как раздел «Настройки бота» в кабинете на сайте
 * (`vpn/app/bot.settings`).
 */
export const Route = createFileRoute('/bot/settings')({
  component: SettingsScreen,
  validateSearch: (s: Record<string, unknown>): { bot?: string } =>
    typeof s.bot === 'string' ? { bot: s.bot } : {},
});

const DEFAULT_LOG_CATEGORIES = {
  new_clients: true,
  topups: true,
  purchases: true,
  renewals: true,
  gifts: true,
  trials: true,
  payouts: true,
  broadcasts: true,
  updates: true,
};

function SettingsScreen() {
  const { bot: botParam } = Route.useSearch();
  const { bot, isPending, isError, refetch } = useSettingsBot(botParam);
  useBackToHub();

  if (isPending) return <SettingsSkeleton />;
  if (isError) return <LoadError onRetry={refetch} />;
  if (!bot) return <NoBots />;
  return <SettingsBody key={bot.id} botId={bot.id} />;
}

function SettingsBody({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: detail, isPending, isError, refetch } = useQuery({
    queryKey: ['bot', botId],
    queryFn: () => api.getBot(botId),
    retry: 1,
  });
  const { data: bots = [] } = useQuery({ queryKey: ['bots'], queryFn: () => api.bots(), staleTime: 60_000 });
  const listBot = bots.find((b) => b.id === botId);

  // Контакт поддержки заливается один раз: тумблеры ниже обновляют кэш бота.
  const [contact, setContact] = useState<string | null>(null);
  const [savedContact, setSavedContact] = useState<string | null>(null);
  useEffect(() => {
    if (!detail || contact !== null) return;
    setContact(detail.support_contact || '');
    setSavedContact(detail.support_contact || '');
  }, [detail, contact]);

  const dirty = contact !== null && contact !== savedContact;
  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: (value: string) => api.updateBot(botId, { support_contact: value }),
    // Сохранённым считаем отправленное, а не текущее: правка, набранная во время
    // запроса, иначе пометилась бы сохранённой и пропала без вопроса при уходе.
    onSuccess: (updated, sent) => {
      setSavedContact(sent);
      qc.setQueryData(['bot', botId], (prev: typeof detail) => prev && { ...prev, ...updated });
      haptic('success');
      toast.success('Сохранено');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось. Попробуйте ещё раз.');
    },
  });
  // У блоков ниже (документы, каналы, лог-канал) свои кнопки, поэтому подпись
  // говорит, что именно сохранится.
  useSaveButton({ dirty, saving, onSave: () => save(contact ?? ''), label: 'Сохранить контакт' });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const { mutate: removeBot, isPending: removing } = useMutation({
    mutationFn: () => api.deleteBot(botId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bots'] });
      haptic('success');
      toast.success('Бот удалён');
      void navigate({ to: '/bots' });
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось удалить бота');
    },
  });

  if (isError) return <LoadError onRetry={() => void refetch()} />;
  if (isPending || !detail || contact === null || !listBot) return <SettingsSkeleton />;

  return (
    <div className="p-4 pb-32 space-y-6">
      <ScreenHeader title="Настройки бота" bot={listBot} />

      <Section id="support" title="Поддержка">
        <Field
          label="Контакт владельца"
          hint="@username или ссылка, например на личку канала t.me/канал?direct, чтобы не светить свой аккаунт. В тикете появится кнопка «Передать владельцу»: клиент получит этот контакт, а тикет у нас закроется. Пусто: кнопки нет."
        >
          <input
            type="text"
            maxLength={128}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="@your_support"
            autoCapitalize="none"
            autoCorrect="off"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-[16px] focus:outline-none focus:border-white/30"
          />
        </Field>
      </Section>

      <Section id="clients" title="Что видят клиенты">
        <div className="space-y-4 divide-y divide-white/[0.05] [&>*:not(:first-child)]:pt-4">
          <WebappToggleCard botId={botId} initial={detail.webapp_enabled !== false} />
          {detail.webapp_enabled !== false ? (
            <MenuButtonCard
              botId={botId}
              savedText={detail.menu_button_text}
              custom={detail.menu_button_custom === true}
            />
          ) : null}
          <CountryPickCard botId={botId} initial={detail.client_country_pick !== false} />
          <StatusPageCard botId={botId} initial={detail.status_page_enabled} />
          <AskLanguageCard botId={botId} initial={detail.ask_language === true} />
          <MirrorsToggleCard botId={botId} initial={detail.mirrors_enabled !== false} />
        </div>
      </Section>

      <Section id="channels" title="Обязательные каналы">
        <RequiredChannelsCard botId={botId} username={detail.username} />
      </Section>

      <Section id="docs" title="Документы">
        <LegalDocsCard botId={botId} />
      </Section>

      <Section id="logs" title="Логи и уведомления">
        <LogChannelCard
          botId={botId}
          username={detail.username}
          initial={{
            log_channel_id: detail.log_channel_id ?? null,
            log_enabled: detail.log_enabled,
            log_auto_disabled: detail.log_auto_disabled ?? false,
            log_categories: detail.log_categories ?? DEFAULT_LOG_CATEGORIES,
            log_topics_enabled: detail.log_topics_enabled ?? false,
          }}
        />
      </Section>

      <Section id="stars" title="Звёзды для рекламы">
        <StarsTopupCard username={detail.username} />
      </Section>

      <Section id="token" title="Токен бота">
        <BotTokenReplace botId={botId} />
      </Section>

      <section id="danger">
        <button
          type="button"
          onClick={() => {
            haptic('warning');
            setConfirmDelete(true);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-[14px] border border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.1)] py-3.5 text-[15px] font-semibold text-danger transition-transform active:scale-[0.98]"
        >
          <Trash2 className="size-[17px]" strokeWidth={1.8} />
          Удалить бота
        </button>
        <p className="mt-2.5 px-2 text-center text-[12px] leading-relaxed text-dim">
          Бот перестанет отвечать. Подписки клиентов останутся до конца срока.
        </p>
      </section>

      <BottomSheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Удалить бота?">
        <p className="text-sm text-muted-foreground">
          @{detail.username} перестанет принимать сообщения. Действующие подписки клиентов
          сохранятся, но продлить их через бота не получится.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="rounded-[14px] bg-white/[0.06] py-3.5 text-sm font-semibold text-foreground transition-transform active:scale-[0.98]"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={removing}
            onClick={() => removeBot()}
            className="flex items-center justify-center gap-2 rounded-[14px] bg-danger py-3.5 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
          >
            {removing && <Loader2 className="size-4 animate-spin" />}
            Удалить
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <SkeletonPage className="p-4 pb-32 space-y-6">
      <div className="px-1 pt-3">
        <h1 className="font-display text-[22px] font-medium">Настройки бота</h1>
        <SkeletonLine size="sm" className="mt-1.5 w-40" />
      </div>
      {[96, 240, 140, 140].map((h, i) => (
        <section key={i}>
          <Skeleton className="mb-2 ml-1 h-3 w-28 rounded" />
          <SkeletonCard className="rounded-[20px]" style={{ minHeight: h }} />
        </section>
      ))}
    </SkeletonPage>
  );
}
